var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc legend
 */
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group, Rect, Text } from '../../element';
import { createSymbol, getSymbolShapeFromCenter } from '../../symbol';
import { ComponentModel } from '../../model';
import { getSymbolSize, getPadding } from '../../utils/styleHelper';
import { horizontalLayout, verticalLayout } from './legendLayout';
import { alignBox } from '../../utils/boxLayout';
import { eventGraph, tooltipEvents } from '../../action';
import { registerComponent } from '../index';
import { brighten } from '../../utils/color';
import { createRichText } from '../../rich';
import measureText from '../../utils/measureText';
import template from '../../utils/template';
/**
 * default
 */
var Legend = /** @class */ (function (_super) {
    __extends(Legend, _super);
    function Legend() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'legend';
        _this.zindex = 99;
        _this._legendListGroupRef = createRef();
        _this._paginationGroupRef = createRef();
        _this._isScrolling = false;
        _this._pageScrollMargin = 12;
        return _this;
    }
    Legend.prevProcessOption = function (option) {
        option.series.forEach(function (seresOption) {
            if (seresOption.visible === undefined) {
                seresOption.visible = true;
            }
            // showLegend default to be undefined
        });
    };
    Legend.prototype.getDefaultOption = function () {
        return {
            show: true,
            scrollable: false,
            scrollLineClamp: 1,
            floating: false,
            width: null,
            height: null,
            align: 'center',
            position: 'bottom',
            backgroundStyle: {
                fill: 'rgba(0, 0, 0, 0)',
                stroke: 'rgba(0, 0, 0, 0)',
                lineWidth: 1,
                borderRadius: 5,
            },
            margin: 24,
            padding: 0,
            itemWidth: null,
            itemHeight: null,
            itemGap: [20, 12],
            offsetX: 0,
            offsetY: 0,
            inactiveColor: '#CCD0E1',
            selectedMode: 'multiple',
            reversed: false,
            textStyle: {
                color: '#6E748E',
                fontSize: 12,
            },
            icon: {
                symbol: 'circle',
                size: 6,
                margin: 8,
                fill: null,
                stroke: null,
                lineWidth: 0,
            },
            title: {
                text: '',
                align: 'center',
                textStyle: {
                    color: '#333333',
                    fontWeight: 'bold',
                    fontSize: 12,
                },
                margin: 12,
            },
            pageIconHorizontal: ['caretLeft', 'caretRight'],
            pageIconVertical: ['caretUp', 'caretDown'],
            pageIconColor: '#4D5369',
            pageIconInactiveColor: '#D6DAEC',
            pageIconSize: [5, 8],
            pageTextStyle: {
                fontSize: 12,
                color: '#878EA8',
            },
            rtl: false,
            allowAllCanceled: true,
        };
    };
    Legend.prototype.getInitialState = function () {
        return {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            currentPage: 0,
            totalPage: 1,
            legendItems: [],
        };
    };
    Legend.prototype.init = function () {
        var _this = this;
        this.registerEvent(tooltipEvents.TOOLTIP_SHOW, function (event) {
            event.points.forEach(function (point) { return _this.formatLegendItem(point); });
        });
        this.registerEvent(tooltipEvents.TOOLTIP_HIDE, function (event) {
            event.points.forEach(function (point) {
                return _this.formatLegendItem(__assign(__assign({}, point), { value: null }));
            });
        });
    };
    Legend.prototype.getLegendItems = function () {
        var _this = this;
        var _a = this.option, itemWidth = _a.itemWidth, itemHeight = _a.itemHeight, textStyle = _a.textStyle, icon = _a.icon, scrollable = _a.scrollable, reversed = _a.reversed;
        var legendList = [];
        var seriesModelList = this.chart.getSeriesByDependency('legend');
        seriesModelList.forEach(function (seriesModel) {
            var seriesIndex = seriesModel.seriesIndex;
            var option = seriesModel.getOption();
            var legendType = option.legendType || seriesModel.legendType;
            var name = option.name, showLegend = option.showLegend, legendGroupField = option.legendGroupField, legendIcon = option.legendIcon;
            var visible = seriesModel.visible();
            var dataSet = seriesModel.getDataset();
            if (showLegend === false) {
                return;
            }
            if (legendType === 'series') {
                legendList.push({
                    name: name,
                    value: null,
                    color: seriesModel.getSeriesColor(),
                    visible: visible,
                    legendType: legendType,
                    legendIcon: legendIcon,
                    seriesIndex: seriesIndex,
                    dataIndex: null,
                    pageIndex: 0,
                });
            }
            else if (legendType === 'datum') {
                dataSet.eachData(function (record, dataIndex) {
                    var encode = seriesModel.getEncoder().getEncodeData(record, dataIndex);
                    legendList.push({
                        name: encode.name,
                        value: encode.value,
                        color: encode.color,
                        visible: !seriesModel.invisibleItems.has(dataIndex),
                        legendType: legendType,
                        legendIcon: legendIcon,
                        seriesIndex: seriesIndex,
                        dataIndex: dataIndex,
                        pageIndex: 0,
                    });
                });
            }
            else if (legendType === 'group') {
                dataSet.uniq(legendGroupField).forEach(function (value) {
                    var dataIndex = dataSet.findIndex(function (item) { return item[legendGroupField] === value; });
                    var record = dataSet.getRecord(dataIndex);
                    var encode = seriesModel.getEncoder().getEncodeData(record, dataIndex);
                    legendList.push({
                        name: value,
                        value: null,
                        color: encode.color,
                        visible: dataSet.getData().some(function (item, index) {
                            return item[legendGroupField] === value && !seriesModel.invisibleItems.has(index);
                        }),
                        legendType: legendType,
                        legendIcon: legendIcon,
                        seriesIndex: seriesIndex,
                        dataIndex: dataIndex,
                        pageIndex: 0,
                    });
                });
            }
            else if (legendType === 'custom') {
                seriesModel.getLegendItems().forEach(function (item) { return legendList.push(item); });
            }
        });
        legendList.forEach(function (legendItem) {
            var _a = measureText(_this.getFormatText(legendItem), textStyle), textLen = _a.width, textHeight = _a.height;
            var itemIcon = __assign(__assign({}, icon), legendItem.legendIcon);
            var _b = getSymbolSize(itemIcon.size), iconWidth = _b[0], iconHeight = _b[1];
            var width = textLen + iconWidth + icon.margin;
            legendItem.width = lodash.isNumber(itemWidth) ? itemWidth : width;
            var itemPadding = scrollable ? 3 : 0;
            legendItem.height = lodash.isNumber(itemHeight)
                ? itemHeight
                : Math.max(iconHeight, textHeight) + itemPadding;
        });
        return reversed ? legendList.reverse() : legendList;
    };
    Legend.prototype.getSideAreaSize = function () {
        var _a = this.option, show = _a.show, floating = _a.floating, position = _a.position, margin = _a.margin;
        var legendItems = this.getLegendItems();
        this.setState({ legendItems: legendItems });
        if (floating || !show || legendItems.length === 0) {
            return {
                side: position,
                size: 0,
            };
        }
        var layout = this.getLegendLayout();
        return {
            side: position,
            size: position === 'top' || position === 'bottom'
                ? layout.height + margin
                : layout.width + margin,
        };
    };
    Legend.prototype.getFormatText = function (point) {
        if (!this.option.formatter) {
            return point.name;
        }
        if (lodash.isString(this.option.formatter)) {
            return template(this.option.formatter, point);
        }
        if (lodash.isFunction(this.option.formatter)) {
            return this.option.formatter(point);
        }
    };
    Legend.prototype.getLegendLayout = function () {
        var legendItems = this.state.legendItems;
        var _a = this.option, position = _a.position, scrollable = _a.scrollable, width = _a.width, height = _a.height, pageTextStyle = _a.pageTextStyle, rtl = _a.rtl;
        var paddingBox = this.chart.getPaddingBox();
        var titleHeight = this.getTitleHeight();
        var legendPadding = getPadding(this.option.padding);
        if (position === 'top' || position === 'bottom') {
            var layoutWidth = lodash.isNumber(width) ? width : paddingBox.width;
            var paginationWidth = scrollable
                ? this.getPaginationSize() + this._pageScrollMargin + 16
                : 0;
            return horizontalLayout({
                layoutWidthLimit: layoutWidth - paginationWidth,
                legendItemList: legendItems,
                option: this.option,
                layoutBox: __assign(__assign({}, legendPadding), { paddingTop: legendPadding.paddingTop + titleHeight }),
                rtl: rtl,
                paginationWidth: paginationWidth,
            });
        }
        if (position === 'left' || position === 'right') {
            var layoutHeight = lodash.isNumber(height) ? height : paddingBox.height;
            return verticalLayout({
                layoutHeightLimit: layoutHeight - (scrollable ? pageTextStyle.fontSize + 20 : 0) - titleHeight,
                legendItemList: legendItems,
                option: this.option,
                layoutBox: __assign(__assign({}, legendPadding), { paddingTop: legendPadding.paddingTop + titleHeight }),
                rtl: rtl,
            });
        }
    };
    Legend.prototype.getMaxPageIndex = function () {
        if (this.state.legendItems.length) {
            return lodash.last(this.state.legendItems).pageIndex;
        }
        return 0;
    };
    Legend.prototype.beforeRender = function () {
        var _a = this.option, show = _a.show, scrollable = _a.scrollable, floating = _a.floating, offsetX = _a.offsetX, offsetY = _a.offsetY, position = _a.position, align = _a.align, rtl = _a.rtl;
        if (!show) {
            return;
        }
        var _b = this.state, legendItems = _b.legendItems, currentPage = _b.currentPage;
        var _c = this.getLegendLayout(), width = _c.width, height = _c.height;
        var x;
        var y;
        var maxPageIndex = this.getMaxPageIndex();
        var paddingBox = this.chart.getPaddingBox();
        var sidePosition = this.chart.getSidePosition(this.id);
        var isHorizontal = position === 'top' || position === 'bottom';
        var isVertical = position === 'left' || position === 'right';
        var hAlign = 'center';
        var verticalAlgin = 'top';
        if (position === 'top' || position === 'bottom') {
            verticalAlgin = position;
            hAlign =
                align === 'start' ? 'left' : align === 'middle' || align === 'center' ? 'center' : 'right';
        }
        else if (position === 'left' || position === 'right') {
            hAlign = position;
            verticalAlgin =
                align === 'start' ? 'top' : align === 'middle' || align === 'center' ? 'middle' : 'bottom';
        }
        var point = alignBox({
            x: paddingBox.left,
            y: paddingBox.top,
            width: paddingBox.width,
            height: paddingBox.height,
        }, width + (scrollable && maxPageIndex > 0 && isHorizontal ? this.getPaginationSize() : 0), height + (scrollable && maxPageIndex > 0 && isVertical ? this.getPaginationSize() : 0), hAlign, verticalAlgin);
        if (floating) {
            x = point.x;
            y = point.y;
        }
        else if (isHorizontal) {
            y = sidePosition + (position === 'bottom' ? -height : 0);
            x = point.x;
        }
        else if (isVertical) {
            x = sidePosition + (position === 'right' ? -width : 0);
            y = point.y;
        }
        x += offsetX;
        y += offsetY;
        this.setState({
            width: width,
            height: height,
            x: x,
            y: y,
            currentPage: currentPage > maxPageIndex ? maxPageIndex : currentPage,
        });
        var paginationWidth = this.getPaginationSize();
        legendItems.forEach(function (item) {
            item.y += y;
            item.x += x;
            if (scrollable) {
                if (position === 'top' || position === 'bottom') {
                    if (rtl) {
                        item.x += paginationWidth;
                        item.x -= width * item.pageIndex;
                    }
                    else {
                        item.x += width * item.pageIndex;
                    }
                }
                else if (position === 'left' || position === 'right') {
                    item.y += height * item.pageIndex;
                }
            }
        });
    };
    Legend.prototype.update = function (nextOption, mergeOld) {
        _super.prototype.update.call(this, nextOption, mergeOld);
        if (!this._isScrolling) {
            this.setState({
                currentPage: 0,
            });
        }
    };
    Legend.prototype.toggleHighlightIcon = function (seriesIndex, dataIndex, active) {
        var _this = this;
        this.state.legendItems.forEach(function (item, index) {
            if (item.seriesIndex === seriesIndex && item.dataIndex === dataIndex && item.visible) {
                var group = _this._legendListGroupRef.current.childAt(index);
                var iconRect = group.childAt(1);
                if (active) {
                    iconRect.setAttr({
                        fill: brighten(item.color),
                    });
                }
                else {
                    iconRect.setAttr({
                        fill: item.color,
                    });
                }
            }
        });
    };
    Legend.prototype.handleLegendItemClick = function (legendItem) {
        var _this = this;
        var _a = this.option, selectedMode = _a.selectedMode, allowAllCanceled = _a.allowAllCanceled;
        if (!selectedMode) {
            return;
        }
        var visible = legendItem.visible;
        var otherLegendItems = this.state.legendItems.filter(function (item) { return item !== legendItem; });
        if (visible && !allowAllCanceled) {
            var isOhterAllInvisible = otherLegendItems.every(function (item) { return !item.visible; });
            if (isOhterAllInvisible) {
                return;
            }
        }
        this._setLegendVisible(legendItem, !visible);
        if (!visible) {
            this.toggleHighlightLegendItem(legendItem, false);
        }
        if (!visible && selectedMode === 'single') {
            otherLegendItems.forEach(function (item) { return _this._setLegendVisible(item, false); });
        }
        this.chart.refresh();
    };
    Legend.prototype._setLegendVisible = function (legendItem, visible) {
        var seriesIndex = legendItem.seriesIndex, dataIndex = legendItem.dataIndex, legendType = legendItem.legendType, name = legendItem.name;
        var seriesModel = this.chart.getSeriesModel(seriesIndex);
        var legendGroupField = seriesModel.getOption().legendGroupField;
        if (legendType === 'series') {
            seriesModel.toggleVisible(visible);
        }
        else if (legendType === 'datum' || legendType === 'custom') {
            seriesModel.togglePointVisible(dataIndex, visible);
        }
        else if (legendType === 'group') {
            seriesModel.getDataset().eachData(function (item, seriesDataIndex) {
                if (item[legendGroupField] === name) {
                    seriesModel.togglePointVisible(seriesDataIndex, visible);
                }
            });
        }
        legendItem.visible = visible;
    };
    Legend.prototype.formatLegendItem = function (point) {
        if (!this.option.show) {
            return;
        }
        if (this.option.textStyle.rich) {
            return;
        }
        var seriesIndex = point.seriesIndex, dataIndex = point.dataIndex, value = point.value;
        var seriesModel = this.chart.getSeriesModel(seriesIndex);
        if (!seriesModel) {
            return;
        }
        var legendTyep = seriesModel.legendType;
        var index;
        if (legendTyep === 'series') {
            index = lodash.findIndex(this.state.legendItems, function (item) { return item.seriesIndex === seriesIndex; });
        }
        else if (legendTyep === 'datum') {
            index = lodash.findIndex(this.state.legendItems, function (item) { return item.seriesIndex === seriesIndex && item.dataIndex === dataIndex; });
        }
        if (index >= 0) {
            var legendItem = this.state.legendItems[index];
            legendItem.value = value;
            legendItem.dataIndex = dataIndex;
            var group = this._legendListGroupRef.current.childAt(index);
            var textGeom = group.childAt(2);
            if (!this.option.formatter) {
                return;
            }
            var text = this.getFormatText(legendItem);
            textGeom.setAttr({
                text: text,
            });
        }
    };
    Legend.prototype.toggleHighlightLegendItem = function (legendItem, active) {
        var seriesIndex = legendItem.seriesIndex, dataIndex = legendItem.dataIndex, visible = legendItem.visible;
        var seriesModel = this.chart.getSeriesModel(seriesIndex);
        var legendGroupField = seriesModel.getOption().legendGroupField;
        if (!visible) {
            return;
        }
        if (legendItem.legendType === 'datum') {
            seriesModel.setState({ hoverPointIndex: active ? dataIndex : null });
            seriesModel.toggleHighlightPoint(dataIndex, active);
        }
        else if (legendItem.legendType === 'series' && legendItem.visible) {
            seriesModel.toggleHighlightSeries(active);
        }
        else if (legendItem.legendType === 'group') {
            seriesModel.getDataset().eachData(function (item, seriesDataIndex) {
                if (item[legendGroupField] === legendItem.name) {
                    seriesModel.toggleHighlightPoint(seriesDataIndex, active);
                }
            });
        }
        this.toggleHighlightIcon(seriesIndex, dataIndex, active);
    };
    Legend.prototype.handleLegendItemMouseEnter = function (legendItem) {
        this.toggleHighlightLegendItem(legendItem, true);
    };
    Legend.prototype.handleLegendItemMouseLeave = function (legendItem) {
        this.toggleHighlightLegendItem(legendItem, false);
    };
    Legend.prototype.scrollToPage = function (next) {
        var _a = this.option, position = _a.position, pageIconInactiveColor = _a.pageIconInactiveColor, pageIconColor = _a.pageIconColor, rtl = _a.rtl;
        var _b = this.state, width = _b.width, height = _b.height, legendItems = _b.legendItems, prevCurrentPage = _b.currentPage;
        var isRightButtonNext = !rtl;
        var currentPage = prevCurrentPage + (next ? 1 : -1);
        var maxPageIndex = lodash.last(legendItems).pageIndex;
        if (currentPage < 0 || currentPage > maxPageIndex) {
            return;
        }
        this.setState({
            currentPage: currentPage,
        });
        var disableColor = pageIconInactiveColor;
        var activeColor = pageIconColor;
        var prevColor = currentPage <= 0 ? disableColor : activeColor;
        var nextColor = currentPage >= maxPageIndex ? disableColor : activeColor;
        this._paginationGroupRef.current.childAt(isRightButtonNext ? 0 : 1).setAttr({
            fill: prevColor,
            stroke: prevColor,
        });
        this._paginationGroupRef.current.childAt(isRightButtonNext ? 1 : 0).setAttr({
            fill: nextColor,
            stroke: nextColor,
        });
        this._paginationGroupRef.current.childAt(2).setAttr({
            text: rtl
                ? maxPageIndex + 1 + "/" + (currentPage + 1)
                : currentPage + 1 + "/" + (maxPageIndex + 1),
        });
        if (position === 'top' || position === 'bottom') {
            if (rtl) {
                this._legendListGroupRef.current.stopAllAnimation().animateTo({
                    position: [width * currentPage, 0],
                });
                // const paginationWidth = this.getPaginationSize();
                // this._clipPath.stopAllAnimation().animateTo({
                //   x: x + paginationWidth - width * currentPage,
                // });
            }
            else {
                this._legendListGroupRef.current.stopAllAnimation().animateTo({
                    position: [width * -currentPage, 0],
                });
                // this._clipPath.stopAllAnimation().animateTo({
                //   x: x + width * currentPage,
                // });
            }
        }
        else if (position === 'left' || position === 'right') {
            this._legendListGroupRef.current.animateTo({
                position: [0, height * -currentPage],
            });
            // this._clipPath.stopAllAnimation().animateTo({
            //   y: y + height * currentPage - clipPadding,
            // });
        }
        this._isScrolling = true;
        this.chart.dispatchAction({
            type: 'legend:scroll',
            currentPage: currentPage,
        });
        this._isScrolling = false;
    };
    Legend.prototype.getPaginationSize = function () {
        var _a = this.option, position = _a.position, pageTextStyle = _a.pageTextStyle, pageIconSize = _a.pageIconSize;
        var textWidth = measureText('9/10', pageTextStyle).width;
        var margin = this._pageScrollMargin;
        if (position === 'top' || position === 'bottom') {
            var arrowWidth = getSymbolSize(pageIconSize)[0];
            return margin + textWidth + arrowWidth * 2;
        }
        return margin + (pageTextStyle.fontSize + 8);
    };
    Legend.prototype.getTitleHeight = function () {
        var _a = this.option.title, text = _a.text, textStyle = _a.textStyle, margin = _a.margin;
        if (text) {
            var height = measureText(text, textStyle).height;
            return height + margin;
        }
        return 0;
    };
    Legend.prototype.renderTitle = function () {
        var group = new Group({
            key: this.getKey('legend-title'),
        });
        var _a = this.option.title, text = _a.text, textStyle = _a.textStyle, align = _a.align;
        if (text) {
            var textAlign = void 0;
            var x = void 0;
            if (align === 'center') {
                textAlign = 'center';
                x = this.state.x + this.state.width / 2;
            }
            else if (align === 'left') {
                x = this.state.x;
                textAlign = 'left';
            }
            else if (align === 'right') {
                x = this.state.x + this.state.width;
                textAlign = 'right';
            }
            group.add(createRichText(__assign(__assign({ text: text,
                x: x, y: this.state.y }, this.getTextStyle(textStyle)), { textAlign: textAlign, textBaseline: 'top', fill: textStyle.color })));
        }
        return group;
    };
    Legend.prototype.renderScrollButton = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('legend-button'),
            ref: this._paginationGroupRef,
        });
        var _a = this.option, position = _a.position, pageIconHorizontal = _a.pageIconHorizontal, pageIconVertical = _a.pageIconVertical, pageIconColor = _a.pageIconColor, pageIconInactiveColor = _a.pageIconInactiveColor, pageTextStyle = _a.pageTextStyle, pageIconSize = _a.pageIconSize, rtl = _a.rtl;
        var _b = this.state, x = _b.x, y = _b.y, width = _b.width, height = _b.height, currentPage = _b.currentPage;
        var maxPageIndex = this.getMaxPageIndex();
        var disableColor = pageIconInactiveColor;
        var activeColor = pageIconColor;
        var pageText = rtl
            ? maxPageIndex + 1 + "/" + (currentPage + 1)
            : currentPage + 1 + "/" + (maxPageIndex + 1);
        var textWidth = measureText(maxPageIndex >= 19 ? '20/20' : '9/10', pageTextStyle).width;
        var disablePrev = currentPage === 0;
        var disableNext = currentPage === maxPageIndex;
        var isRightButtonNext = !rtl;
        var leftButtonFill = (!isRightButtonNext ? disableNext : disablePrev)
            ? disableColor
            : activeColor;
        var rightButtonFill = (isRightButtonNext ? disableNext : disablePrev)
            ? disableColor
            : activeColor;
        var margin = this._pageScrollMargin;
        if (maxPageIndex === 0) {
            return group;
        }
        if (position === 'top' || position === 'bottom') {
            var cy = y + height / 2;
            var _c = getSymbolSize(pageIconSize), arrowWidth = _c[0], arrowHeight = _c[1];
            var prevIcon = pageIconHorizontal[0], nextIcon = pageIconHorizontal[1];
            var leftButton = createSymbol({ symbol: prevIcon }, {
                x: rtl ? x : x + width + margin,
                y: cy - arrowHeight / 2,
                width: arrowWidth,
                height: arrowHeight,
            }, {
                fill: leftButtonFill,
                stroke: leftButtonFill,
                lineWidth: 0,
                cursor: 'pointer',
                pointerEvents: 'bounding-box',
                onClick: function () { return _this.scrollToPage(!isRightButtonNext); },
            });
            var rightButton = createSymbol({ symbol: nextIcon }, {
                x: (rtl ? x : x + width + margin) + arrowWidth + textWidth,
                y: cy - arrowHeight / 2,
                width: arrowWidth,
                height: arrowHeight,
            }, {
                fill: rightButtonFill,
                stroke: rightButtonFill,
                lineWidth: 0,
                cursor: 'pointer',
                pointerEvents: 'bounding-box',
                onClick: function () { return _this.scrollToPage(isRightButtonNext); },
            });
            var text = new Text(__assign({ x: (rtl ? x : x + width + margin) + arrowWidth + textWidth / 2, y: cy, textBaseline: 'middle', textAlign: 'center', text: pageText }, this.getTextStyle(pageTextStyle)));
            group.addAll([leftButton, rightButton, text]);
        }
        else if (position === 'left' || position === 'right') {
            var _d = !rtl ? pageIconVertical : pageIconVertical.slice().reverse(), prevIcon = _d[0], nextIcon = _d[1];
            var lineHeight = pageTextStyle.fontSize + 8;
            var cy = y + height + margin + lineHeight / 2;
            var cx = x + width / 2;
            var _e = getSymbolSize(pageIconSize), arrowHeight = _e[0], arrowWidth = _e[1];
            var leftButton = createSymbol({ symbol: prevIcon }, {
                x: cx - textWidth / 2 - arrowWidth,
                y: cy - arrowHeight / 2,
                width: arrowWidth,
                height: arrowHeight,
            }, {
                fill: leftButtonFill,
                cursor: 'pointer',
                onClick: function () { return _this.scrollToPage(!isRightButtonNext); },
            });
            var rightButton = createSymbol({ symbol: nextIcon }, {
                x: cx + textWidth / 2,
                y: cy - arrowHeight / 2,
                width: arrowWidth,
                height: arrowHeight,
            }, {
                fill: rightButtonFill,
                cursor: 'pointer',
                onClick: function () { return _this.scrollToPage(isRightButtonNext); },
            });
            var text = new Text(__assign({ x: cx, y: cy, textAlign: 'center', textBaseline: 'middle', text: pageText }, this.getTextStyle(pageTextStyle)));
            group.addAll([leftButton, rightButton, text]);
        }
        return group;
    };
    Legend.prototype.render = function () {
        var _this = this;
        var _a = this.option, show = _a.show, position = _a.position, scrollable = _a.scrollable, backgroundStyle = _a.backgroundStyle, rtl = _a.rtl, icon = _a.icon;
        if (!show) {
            return null;
        }
        var currentPage = this.state.currentPage;
        var isHorizontal = position === 'top' || position === 'bottom';
        var translate = isHorizontal
            ? [-currentPage * this.state.width, 0]
            : [0, -currentPage * this.state.height];
        var group = new Group({
            display: this.option.show,
            key: this.getKey('legend'),
        });
        // legend out box
        var legendRect = new Rect(__assign(__assign({ x: this.state.x, y: this.state.y, width: this.state.width, height: this.state.height }, this.getShapeStyle(backgroundStyle)), { r: backgroundStyle.borderRadius }));
        group.add(legendRect);
        group.add(this.renderTitle());
        var legendClipRect = new Rect({
            x: this.state.x,
            y: this.state.y,
            width: this.state.width,
            height: this.state.height,
        });
        var clipGroup = new Group({
            key: this.getKey('legend-clip'),
            clip: scrollable ? legendClipRect : null,
        });
        var legendListGroup = new Group({
            key: this.getKey('legend-list'),
            ref: this._legendListGroupRef,
            position: translate,
        });
        clipGroup.add(legendListGroup);
        var legendGeomList = this.state.legendItems.map(function (legendItem, index) {
            var x = legendItem.x, y = legendItem.y, width = legendItem.width, height = legendItem.height, legendIcon = legendItem.legendIcon;
            var itemIcon = __assign(__assign({}, icon), legendIcon);
            var margin = _this.option.icon.margin;
            var iconWidth = getSymbolSize(itemIcon.size)[0];
            var itemGroup = new Group({
                key: _this.getKey("legend-item" + index),
                cursor: 'pointer',
                onClick: function () { return _this.handleLegendItemClick(legendItem); },
                onMouseEnter: function () { return _this.handleLegendItemMouseEnter(legendItem); },
                onMouseLeave: function () { return _this.handleLegendItemMouseLeave(legendItem); },
                onEvent: function (event) {
                    return _this.handleUIEvent(event, eventGraph.LEGEND_ITEM, {
                        legendItem: legendItem,
                    });
                },
            });
            // legend item event rect box
            itemGroup.add(new Rect({
                x: x,
                y: y,
                width: width,
                height: height,
                fill: 'rgba(0, 0, 0, 0)',
                lineWidth: 0,
                cursor: _this.option.selectedMode ? 'pointer' : 'auto',
            }));
            // legend item text
            var symbolBox = getSymbolShapeFromCenter(rtl ? x + width - iconWidth / 2 : x + iconWidth / 2, y + height / 2, itemIcon.size, itemIcon.borderRadius);
            itemGroup.add(createSymbol(itemIcon, symbolBox, __assign(__assign({ cursor: 'pointer', stroke: legendItem.visible ? legendItem.color : _this.option.inactiveColor }, _this.getShapeStyle(itemIcon)), { r: itemIcon.borderRadius, fill: legendItem.visible ? legendItem.color : _this.option.inactiveColor, onEvent: function (event) {
                    return _this.handleUIEvent(event, eventGraph.LEGETEN_ICON, {
                        legendItem: legendItem,
                    });
                } })));
            itemGroup.add(createRichText(__assign(__assign({ text: _this.getFormatText(legendItem), x: rtl ? x + width - iconWidth - margin : x + margin + iconWidth, y: y + height / 2 }, _this.getTextStyle(_this.option.textStyle)), { textAlign: rtl ? 'right' : 'left', textBaseline: 'middle', fill: legendItem.visible ? _this.option.textStyle.color : _this.option.inactiveColor, cursor: 'pointer', onEvent: function (event) {
                    return _this.handleUIEvent(event, eventGraph.LEGEND_LABEL, {
                        legendItem: legendItem,
                    });
                } })));
            return itemGroup;
        });
        legendListGroup.addAll(legendGeomList);
        group.add(clipGroup);
        if (this.option.scrollable) {
            group.add(this.renderScrollButton());
        }
        return group;
    };
    return Legend;
}(ComponentModel));
export default Legend;
registerComponent('legend', Legend);
